import React from 'react';

import { formatDateTimeValue } from '~libs/dayjs';
import { getNestedValue } from '~libs/records';
import { Row } from '~libs/reduxUtils';

import FormField from './Field';
import { FieldProp } from './props';

export interface Definition extends FieldProp {
  key: string;
}

interface ComponentProps {
  authTag?: string;
  definitions: Array<Definition>;
  row?: Row;
  values: { [key: string]: string | number | boolean | object | null };
  invalidFields?: string[];
  size?: 'sm' | 'lg';
  setFieldValue?: any;
  setFieldTouched?: any;
  touched?: any;
}

const FormFields: React.FC<ComponentProps> = ({
  authTag,
  definitions,
  row,
  values,
  invalidFields = [],
  size = 'lg',
  setFieldValue,
  setFieldTouched,
  touched,
}) => {
  // Build fields systematically based on form fields
  const groupedDefinitions = definitions.reduce(
    (acc, def, index) => {
      if (def.order == null) {
        // No order → each field gets its own row, respecting definition order
        acc[`__noorder_${index}`] = [def];
      } else {
        const { order } = def;
        if (!acc[order]) acc[order] = [];
        acc[order].push(def);
      }
      return acc;
    },
    {} as Record<string | number, Definition[]>,
  );

  const dateTimeTypes = ['date', 'time'];

  const renderField = (definition: Definition, index: number) => {
    const {
      key,
      type,
      className,
      field,
      show,
      description,
      deriveDescription,
      link,
      deriveLink,
      warning,
      deriveWarning,
      initialOption,
      initialiseInitialValue,
      initialValue,
      deriveValue,
      selectorParams,
      dependencies,
      disabled,
      ...rest
    } = definition;

    const onChange = async (name: string, value: any) => {
      setFieldValue(name, value);
      setFieldTouched(name, true);

      if (rest.onChange) {
        const changeValues = await rest.onChange({ ...values, [name]: value });
        if (changeValues) {
          Object.entries(changeValues).forEach(([key, value]) => setFieldValue(key, value));
        }
      }
    };

    const id = field ?? key;
    const functionValues = { ...values, touched };

    // Derive value
    let value = values[id];
    if (id.includes('.') && row) value = getNestedValue(id, row);
    if (type && dateTimeTypes.includes(type)) value = formatDateTimeValue(type, value);
    if (dependencies && dependencies.length > 0) {
      const dependant: any = {};
      dependencies.forEach((id: string) => {
        dependant[id] = values[id];
      });
      value = dependant;
    }

    if (deriveValue) value = deriveValue(functionValues);

    const derivedInitialOption =
      initialOption && row && initialOption.name === '' ? row[initialOption.id] : initialOption;
    const computedInitialValue = initialiseInitialValue?.(functionValues);

    return (
      <div key={index} className={`px-2`} style={{ flex: '1 1 0', minWidth: 0 }}>
        <FormField
          {...rest}
          authTag={authTag}
          type={type}
          size={size}
          id={id}
          fieldId={key}
          value={value}
          selectorParams={typeof selectorParams === 'function' ? selectorParams(functionValues) : selectorParams}
          initialOption={derivedInitialOption}
          initialValue={computedInitialValue}
          disabled={typeof disabled === 'function' ? disabled(functionValues) : disabled}
          show={typeof show === 'function' ? show(functionValues) : show}
          description={typeof deriveDescription === 'function' ? deriveDescription(functionValues) : description}
          link={typeof deriveLink === 'function' ? deriveLink(functionValues) : link}
          warning={typeof deriveWarning === 'function' ? deriveWarning(functionValues) : warning}
          invalid={invalidFields.includes(id)}
          className={`p-2 ps-4 pe-4${className ? ` ${className}` : ''}`}
          handleChange={onChange}
          setFieldValue={setFieldValue}
        />
      </div>
    );
  };

  // Render grouped rows
  const rows = Object.entries(groupedDefinitions)
    .sort(([a], [b]) => {
      const isNumA = !isNaN(Number(a));
      const isNumB = !isNaN(Number(b));
      if (isNumA && isNumB) return Number(a) - Number(b);
      if (isNumA) return -1;
      if (isNumB) return 1;

      // for no-order groups, preserve index order
      return a.localeCompare(b, undefined, { numeric: true });
    })
    .map(([order, group]) => {
      return (
        <div
          key={order}
          className="d-flex"
          style={{
            width: '100%',
            gap: '0.5rem',
          }}
        >
          {group.map((def, i) => renderField(def, i))}
        </div>
      );
    });

  return <>{rows}</>;
};

export default FormFields;
